import { useEffect, useState, useRef } from "react";

type UseRecorderReturn = [
  string,                  // audioURL
  boolean,                 // isRecording
  Blob | null,             // audioFile
  () => void,              // startRecording
  () => void,              // stopRecording
  React.Dispatch<React.SetStateAction<string>> // setAudioURL
];

const useRecorder = (initialURL: string = "") => {
  const [audioURL, setAudioURL] = useState<string>(initialURL);
  const [audioFile, setAudioFile] = useState<Blob | null>(null);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [recorder, setRecorder] = useState<MediaRecorder | null>(null);

  // Keep chunks across renders
  const chunksRef = useRef<Blob[]>([]);

  useEffect(() => {
    if (recorder === null) {
      if (isRecording) {
        requestRecorder().then(setRecorder).catch(console.error);
      }
      return;
    }

    const handleData = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    const handleStop = () => {
      const completeBlob = new Blob(chunksRef.current, { type: recorder.mimeType });
      chunksRef.current = []; // reset for next recording
      setAudioFile(completeBlob);
      setAudioURL(URL.createObjectURL(completeBlob));
    };

    recorder.addEventListener("dataavailable", handleData);
    recorder.addEventListener("stop", handleStop);

    if (isRecording) {
      try {
        recorder.start(1000); // Safari needs timeslice for steady data
      } catch (err) {
        console.error("Recorder start failed:", err);
      }
    } else {
      if (recorder.state !== "inactive") {
        recorder.stop();
      }
    }

    return () => {
      recorder.removeEventListener("dataavailable", handleData);
      recorder.removeEventListener("stop", handleStop);
    };
  }, [recorder, isRecording]);

  const startRecording = () => {
    chunksRef.current = [];
    setAudioURL("");
    setAudioFile(null);
    setIsRecording(true);
  };

  const stopRecording = () => setIsRecording(false);

  return [audioURL, isRecording, audioFile, startRecording, stopRecording, setAudioURL] as UseRecorderReturn;
};

async function requestRecorder() {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

  let options: MediaRecorderOptions = {};

  if (MediaRecorder.isTypeSupported("audio/mp4")) {
    options.mimeType = "audio/mp4";
  } else if (MediaRecorder.isTypeSupported("audio/webm")) {
    options.mimeType = "audio/webm";
  } else if (MediaRecorder.isTypeSupported("audio/ogg")) {
    options.mimeType = "audio/ogg";
  }

  return new MediaRecorder(stream, options);
}

export default useRecorder;
